import * as yup from 'yup';
import { isValidYupSchema, yupUtils } from '../../utils/yup/utils';
import { CONTENT_TYPES } from '../types/others';
import { Lesson, LessonStatus, POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from '../types/lesson';
import { ProjectErrors, ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorForCode, getProjectErrorFromRawError, isProjectError } from '../../utils/errors';

export const PollSchema = yup
  .object()
  .shape({
    question: yupUtils.stringRequired('Poll question is a required field'),
    options: yup.array().of(yup.string()).min(POLL_MIN_OPTIONS).max(POLL_MAX_OPTIONS),
  })
  .optional()
  .nullable()
  .default(undefined);

export const MinimalLessonSchema = yup.object().shape({
  title: yupUtils.stringRequired('Title is a required field'),
  lengthMinutes: yupUtils.stringOptionalNullable,
  lengthSeconds: yupUtils.stringOptionalNullable,
  category: yupUtils.stringOptionalNullable,
  pillarType: yupUtils.stringOptionalNullable,
  pillarExpertise: yupUtils.stringOptionalNullable,
});

export const LessonSchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  createdAt: yupUtils.numberRequired,
  updatedAt: yupUtils.numberRequired,
  title: yupUtils.stringRequired('Title is a required field'),
  totalLength: yupUtils.numberOptionalNullable,
  category: yupUtils.stringOptionalNullable,
  pillarType: yupUtils.stringOptionalNullable,
  pillarExpertise: yupUtils.stringOptionalNullable,
  imagePath: yupUtils.stringOptionalNullable,
  audioStreamPath: yupUtils.stringOptionalNullable,
  videoStreamPath: yupUtils.stringOptionalNullable,
  contentType: yupUtils.stringNullableOneOf(CONTENT_TYPES),
  keyTakeaways: yupUtils.arrayOptionalNullableOf(yup.string()),
  publishedAt: yupUtils.numberOptionalNullable,
  isDailyLesson: yupUtils.booleanOptionalNullable,
  isRagsToRichesStory: yupUtils.booleanOptionalNullable,
  isSleepStory: yupUtils.booleanOptionalNullable,
  isSpecialStory: yupUtils.booleanOptionalNullable,
  isWisdomStory: yupUtils.booleanOptionalNullable,
  isQuestionAndAnswer: yupUtils.booleanOptionalNullable,
  tags: yupUtils.arrayOptionalNullableOf(yup.string()),
  listenReason: yupUtils.stringOptionalNullable,
  poll: PollSchema,
});

export const ReadyToPlayLessonSchema = yup.object().shape(
  {
    id: yupUtils.stringRequired(),
    createdAt: yupUtils.numberRequired,
    updatedAt: yupUtils.numberRequired,
    title: yupUtils.stringRequired(),
    totalLength: yupUtils.numberOptionalNullable,
    category: yupUtils.stringOptionalNullable,
    pillarType: yupUtils.stringOptionalNullable,
    pillarExpertise: yupUtils.stringOptionalNullable,
    imagePath: yupUtils.stringOptionalNullable,
    audioStreamPath: yup
      .string()
      .when('videoStreamPath', {
        is: undefined,
        then: yupUtils.stringRequired(),
        otherwise: yupUtils.stringOptionalNullable,
      })
      .when('videoStreamPath', {
        is: null,
        then: yupUtils.stringRequired(),
        otherwise: yupUtils.stringOptionalNullable,
      }),
    videoStreamPath: yup
      .string()
      .when('audioStreamPath', {
        is: undefined,
        then: yupUtils.stringRequired(),
        otherwise: yupUtils.stringOptionalNullable,
      })
      .when('audioStreamPath', {
        is: null,
        then: yupUtils.stringRequired(),
        otherwise: yupUtils.stringOptionalNullable,
      }),
    contentType: yupUtils.stringNullableOneOf(CONTENT_TYPES),
    keyTakeaways: yupUtils.arrayOptionalNullableOf(yup.string()),
    tags: yupUtils.arrayOptionalNullableOf(yup.string()),
    listenReason: yupUtils.stringOptionalNullable,
  },
  [['audioStreamPath', 'videoStreamPath']],
);

const processLesson = async (lesson: any): Promise<Lesson> => {
  let status: LessonStatus = 'minimal';

  try {
    if (await isValidYupSchema(ReadyToPlayLessonSchema, lesson)) {
      status = 'readyToPlay';
    }
  } catch (e) {}

  return {
    ...lesson,
    status,
    publishedAtDate: lesson?.publishedAt ? new Date(lesson?.publishedAt) : undefined,
  };
};

export const parseLesson = async (object: any): Promise<Lesson | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(LessonSchema, object)) {
      return processLesson(object as Lesson);
    }
    return getProjectErrorForCode(ProjectErrors.invalidLesson);
  } catch (error) {
    console.log(`[LESSONS] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const parseLessons = async (items: any): Promise<Lesson[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[LESSONS] Error: No array found.');
    return [];
  }

  const data: Lesson[] = [];
  for (const item of items) {
    const o = await parseLesson(item);
    if (o && !isProjectError(o)) {
      data.push(o as Lesson);
    }
  }

  return data;
};
